import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, Card, KpiCard, Ring, StatusPill, SegmentedControl, ProgressBar, WORK_ORDER_STATUS, cn } from '@careandkey/shared';
import { supabase } from '../lib/supabase';
import { Layout, Topbar } from '../components/Layout';
import { fmtDateShort, initials } from '../lib/fmt';
import { useOrg } from '../lib/org';
import { WorkOrderForm, type WorkOrderRow } from '../components/EntityForms';
import { WorkOrderCosts } from '../components/WorkOrderCosts';

type WO = {
  id: string; wo_number: string | null; wo_type: string; priority: string; status: string;
  created_at: string; completed_at: string | null; sla_resolution_minutes: number | null;
  visit_count: number; assigned_to: string | null; property_id: string; asset_id: string | null;
  billing_status: string | null;
  properties: { name: string } | null; assets: { name: string; serial_number: string | null; agent_type: string | null } | null;
};

type CLItem = { id: string; position: number; label: string; item_type: string; unit: string | null; reading_value: number | null; done: boolean; is_anomaly: boolean; photo_required: boolean };

const OPEN_STATUSES = ['open', 'acknowledged', 'assigned', 'in_progress'];
const isOverdueWO = (o: WO) =>
  !o.completed_at && o.sla_resolution_minutes != null &&
  new Date(o.created_at).getTime() + o.sla_resolution_minutes * 60000 < Date.now();

export function WorkOrders() {
  const { t, i18n } = useTranslation();
  const { timezone } = useOrg();
  const [orders, setOrders] = useState<WO[]>([]);
  const [obl, setObl] = useState<{ status: string }[]>([]);
  const [members, setMembers] = useState<Record<string, string>>({});
  const [params] = useSearchParams();
  const woParam = params.get('wo');
  const [selected, setSelected] = useState<string | null>(null);
  const [tab, setTab] = useState(3);
  const [kpiOpen, setKpiOpen] = useState(true);
  const [propertyId, setPropertyId] = useState('');
  const [priority, setPriority] = useState('');
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<WorkOrderRow | null>(null);

  const [clItems, setClItems] = useState<CLItem[]>([]);

  const load = useCallback(async () => {
    const [wo, ob] = await Promise.all([
      supabase.from('work_orders')
        .select('id,wo_number,wo_type,priority,status,created_at,completed_at,sla_resolution_minutes,visit_count,assigned_to,property_id,asset_id,billing_status,properties(name),assets(name,serial_number,agent_type)')
        .is('deleted_at', null).order('created_at', { ascending: false }),
      supabase.from('asset_obligations').select('status').is('deleted_at', null),
    ]);
    const list = (wo.data as unknown as WO[]) ?? [];
    setOrders(list);
    setObl((ob.data as { status: string }[]) ?? []);
    setSelected((cur) => cur ?? woParam ?? list[0]?.id ?? null);
    // resolve assignee user ids → display names (never show id-derived initials)
    const prof = await supabase.from('user_profiles').select('user_id,display_name').is('deleted_at', null);
    setMembers(Object.fromEntries(((prof.data as any[]) ?? []).map((p) => [p.user_id, p.display_name])));
    setLoading(false);
  }, []);
  const assigneeName = useCallback((uid: string | null) => (uid ? members[uid] ?? null : null), [members]);
  useEffect(() => { load(); }, [load]);

  // attached checklist for the selected work order (read-only here; filled in the field)
  useEffect(() => {
    if (!selected) { setClItems([]); return; }
    (async () => {
      const cl = await supabase.from('work_order_checklists').select('id').eq('work_order_id', selected).is('deleted_at', null).limit(1).maybeSingle();
      if (!cl.data) { setClItems([]); return; }
      const it = await supabase.from('work_order_checklist_items')
        .select('id,position,label,item_type,unit,reading_value,done,is_anomaly,photo_required')
        .eq('wo_checklist_id', (cl.data as any).id).is('deleted_at', null).order('position');
      setClItems((it.data as CLItem[]) ?? []);
    })();
  }, [selected]);

  const kpis = useMemo(() => {
    const open = orders.filter((o) => OPEN_STATUSES.includes(o.status)).length;
    const overdueWO = orders.filter(isOverdueWO).length;
    const completed = orders.filter((o) => o.completed_at);
    const slaMet = completed.length
      ? Math.round(100 * completed.filter((o) => o.sla_resolution_minutes != null && new Date(o.completed_at!).getTime() <= new Date(o.created_at).getTime() + o.sla_resolution_minutes * 60000).length / completed.length)
      : null;
    const total = obl.length || 1;
    const compliance = Math.round(((total - obl.filter((o) => o.status === 'overdue').length) / total) * 100);
    return { open, overdueWO, slaMet, compliance };
  }, [orders, obl]);

  const tabDefs = [
    { label: t('wo.toDo'), match: (o: WO) => OPEN_STATUSES.includes(o.status) && o.status !== 'in_progress' },
    { label: t('wo.inProgress'), match: (o: WO) => o.status === 'in_progress' || o.status === 'assigned' },
    { label: t('wo.done'), match: (o: WO) => ['completed', 'validated', 'closed'].includes(o.status) },
    { label: t('common.all'), match: () => true },
  ];
  const byFilters = (o: WO) => (!propertyId || o.property_id === propertyId) && (!priority || o.priority === priority);
  const tabCount = (m: (o: WO) => boolean) => orders.filter((o) => m(o) && byFilters(o)).length;
  const visible = orders.filter((o) => (tabDefs[tab]?.match ?? (() => true))(o) && byFilters(o));
  const current = orders.find((o) => o.id === selected) ?? null;

  const properties = useMemo(() => {
    const m = new Map<string, string>();
    orders.forEach((o) => o.properties && m.set(o.property_id, o.properties.name));
    return [...m.entries()];
  }, [orders]);

  const title = (o: WO) => `${t('woType.' + o.wo_type)} — ${o.assets?.name ?? t('nav.assets')}`;
  const sel = 'rounded-md border border-border bg-card px-2.5 py-1 text-sm text-[#344054] outline-none';

  return (
    <Layout header={<Topbar actions={<><Button variant="secondary">{t('common.export')}</Button><Button onClick={() => { setEditing(null); setFormOpen(true); }}>+ {t('wo.newWorkOrder')}</Button></>} />}>
      <div className="flex flex-col gap-[18px] px-7 pt-6">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div className="flex flex-col gap-1">
            <span className="text-xs uppercase tracking-[0.12em] text-muted">
              {new Date().toLocaleDateString(i18n.language, { weekday: 'long', day: 'numeric', month: 'long', timeZone: timezone })}
            </span>
            <h1 className="text-[30px] font-semibold tracking-[-0.02em]">{t('wo.title')}</h1>
          </div>
          <div className="flex items-center gap-3">
            <SegmentedControl
              items={tabDefs.map((x) => ({ label: <span>{x.label} <span className="font-mono text-xs text-muted">{tabCount(x.match)}</span></span> }))}
              active={tab}
              onChange={setTab}
            />
            <Button variant="ghost" size="sm" onClick={() => setKpiOpen((v) => !v)}>{kpiOpen ? t('common.hideKpis') : t('common.showKpis')}</Button>
          </div>
        </div>

        {kpiOpen && (
          <section className="grid grid-cols-1 gap-3.5 sm:grid-cols-2 lg:grid-cols-4">
            <KpiCard label={t('kpi.preventiveCompliance')} value={`${kpis.compliance}%`} sub={`${t('kpi.target')} 95%`} dark>
              <Ring value={kpis.compliance} onDark label="" />
            </KpiCard>
            <KpiCard label={t('kpi.openWorkOrders')} value={kpis.open} />
            <KpiCard label={t('kpi.slaMet30')} value={kpis.slaMet == null ? '—' : `${kpis.slaMet}%`} sub={kpis.slaMet == null ? t('common.noData') : undefined} />
            <KpiCard label={t('kpi.overdue')} value={<span className={kpis.overdueWO ? 'text-st-overdue-fg' : undefined}>{kpis.overdueWO}</span>} />
          </section>
        )}
      </div>

      <div className="flex flex-wrap items-start gap-[18px] px-7 pb-7 pt-[18px]">
        <Card className="min-w-0 max-w-[460px] flex-1 overflow-hidden p-0" style={{ flexBasis: 380 }}>
          <div className="flex flex-wrap gap-2 border-b border-divider p-4">
            <select className={sel} value={propertyId} onChange={(e) => setPropertyId(e.target.value)}>
              <option value="">{t('filters.property')}</option>
              {properties.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
            </select>
            <select className={sel} value={priority} onChange={(e) => setPriority(e.target.value)}>
              <option value="">{t('filters.priority')}</option>
              {['low', 'medium', 'high', 'urgent'].map((p) => <option key={p} value={p}>{t('priority.' + p)}</option>)}
            </select>
          </div>
          {loading ? (
            <div className="p-6 text-sm text-muted">{t('common.loading')}</div>
          ) : visible.length === 0 ? (
            <div className="p-6 text-sm text-muted">{t('common.noData')}</div>
          ) : (
            visible.map((o) => {
              const sd = WORK_ORDER_STATUS[o.status];
              const active = o.id === selected;
              return (
                <button key={o.id} onClick={() => setSelected(o.id)}
                  className={cn('flex w-full flex-col gap-2 border-b border-divider p-4 text-left last:border-0', active ? 'bg-[#F5F7FF] shadow-[inset_3px_0_0_var(--pv-accent)]' : 'bg-card hover:bg-bg')}>
                  <div className="flex items-start justify-between gap-2.5">
                    <span className="text-sm font-medium leading-snug">
                      <span className="font-mono text-xs text-muted">{o.wo_number ?? '—'}</span> · {title(o)}
                    </span>
                    <span className="whitespace-nowrap font-mono text-xs text-muted">{fmtDateShort(o.created_at, i18n.language, timezone)}</span>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    {sd && <StatusPill tone={sd.tone}>{t(sd.labelKey)}</StatusPill>}
                    <span className="text-sm text-muted">{o.properties?.name}</span>
                    {o.assigned_to && (
                      <span title={assigneeName(o.assigned_to) ?? undefined} className="ml-auto grid h-6 w-6 place-items-center rounded-full bg-accent/10 font-mono text-[10px] font-semibold text-accent">
                        {initials(assigneeName(o.assigned_to))}
                      </span>
                    )}
                  </div>
                </button>
              );
            })
          )}
        </Card>

        {current && (
          <Card className="min-w-0 flex-[2] overflow-hidden p-0" style={{ flexBasis: 480 }}>
            <div className="flex flex-col gap-3.5 bg-chrome p-6 text-white">
              <div className="flex items-start justify-between gap-3">
                <div className="flex min-w-0 flex-col gap-1.5">
                  <span className="font-mono text-xs text-chrome-muted">{current.wo_number ?? '—'} · {t('woType.' + current.wo_type).toUpperCase()}</span>
                  <h2 className="text-[22px] font-semibold tracking-tight">{title(current)}</h2>
                  <span className="text-chrome-muted">
                    {current.properties?.name}
                    {current.assets?.serial_number ? ` · ${current.assets.serial_number}` : current.assets?.name ? ` · ${current.assets.name}` : ''}
                  </span>
                </div>
                <div className="flex flex-col items-end gap-2">
                  <Button variant="secondary" size="sm" onDark onClick={() => { setEditing({ id: current.id, wo_type: current.wo_type, priority: current.priority, property_id: current.property_id, asset_id: current.asset_id, assigned_to: current.assigned_to }); setFormOpen(true); }}>{t('form.edit')}</Button>
                  {current.billing_status && <span className={cn('rounded-pill px-2 py-0.5 text-2xs font-medium', current.billing_status === 'included' ? 'bg-dk-ok-bg text-dk-ok-fg' : current.billing_status === 'billable' ? 'bg-[#2A2140] text-[#C9B8FF]' : 'bg-chrome-2 text-chrome-muted')}>{t('wo.billing_' + current.billing_status)}</span>}
                </div>
              </div>
              <div className="grid grid-cols-4 gap-1.5 rounded-[10px] bg-chrome-2 p-1">
                {['open', 'on_hold', 'in_progress', 'done'].map((s) => {
                  const act = (s === 'in_progress' && current.status === 'in_progress') ||
                    (s === 'open' && OPEN_STATUSES.includes(current.status) && current.status !== 'in_progress') ||
                    (s === 'done' && ['completed', 'validated', 'closed'].includes(current.status));
                  return <button key={s} className={cn('rounded-lg py-2.5 text-sm', act ? 'bg-accent font-medium text-white' : 'text-chrome-muted')}>{t('wo.st_' + s)}</button>;
                })}
              </div>
            </div>

            <div className="grid grid-cols-3 gap-x-6 gap-y-[18px] border-b border-divider p-6">
              {[
                [t('wo.assignedTo'), assigneeName(current.assigned_to) ?? '—'],
                [t('wo.priority'), t('priority.' + current.priority)],
                [t('wo.asset'), current.assets?.name ?? '—'],
                [t('wo.created'), fmtDateShort(current.created_at, i18n.language, timezone)],
                [t('wo.visits'), String(current.visit_count)],
                [t('wo.type'), t('woType.' + current.wo_type)],
              ].map(([k, v]) => (
                <div key={k} className="flex flex-col gap-1"><span className="text-xs text-muted">{k}</span><span className="font-medium">{v}</span></div>
              ))}
            </div>

            <div className="flex flex-col gap-1.5 p-6">
              <div className="mb-1.5 flex items-center justify-between">
                <h3 className="text-[15px] font-semibold">{t('wo.checklist')}</h3>
                {clItems.length > 0 && (() => {
                  const done = clItems.filter((i) => i.done).length;
                  return <div className="flex items-center gap-2.5"><div className="w-[120px]"><ProgressBar value={clItems.length ? (done / clItems.length) * 100 : 0} /></div><span className="font-mono text-xs text-muted">{done}/{clItems.length}</span></div>;
                })()}
              </div>
              {clItems.length === 0 ? (
                <div className="rounded-[10px] bg-[#F7F8FA] p-4 text-sm text-muted">{t('wo.noChecklistAttached')}</div>
              ) : (
                <div className="flex flex-col gap-2">
                  {clItems.map((i) => (
                    <div key={i.id} className={cn('flex items-center gap-3 rounded-[10px] border p-3', i.is_anomaly ? 'border-st-overdue-fg/40 bg-st-overdue-bg' : 'border-divider bg-card')}>
                      <span className={cn('grid h-5 w-5 shrink-0 place-items-center rounded-[6px] text-[12px]', i.done ? 'bg-accent text-white' : 'border border-[#C9CFD8]')}>{i.done ? '✓' : ''}</span>
                      <span className="flex-1 text-sm">{i.label}<span className="ml-2 text-xs text-muted">{t('checklistType.' + i.item_type)}</span></span>
                      {i.item_type === 'reading' && i.reading_value != null && (
                        <span className={cn('font-mono text-sm', i.is_anomaly ? 'text-st-overdue-fg' : 'text-st-done-fg')}>{i.reading_value}{i.unit ? ` ${i.unit}` : ''}</span>
                      )}
                      {i.is_anomaly && <StatusPill tone="overdue">{t('woChecklist.anomaly')}</StatusPill>}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <WorkOrderCosts workOrderId={current.id} propertyId={current.property_id} />
          </Card>
        )}
      </div>
      <WorkOrderForm open={formOpen} onClose={() => setFormOpen(false)} onSaved={load} initial={editing} />
    </Layout>
  );
}
